import { Component, computed, input, output } from '@angular/core';
import { ButtonComponent } from '../button/button.component';
import { type DateNavigatorColor, type DateNavigatorSize } from './date-navigator.model';

@Component({
  selector: 'lib-date-navigator',
  imports: [ButtonComponent],
  styleUrls: [
    '../../tokens/colors.css',
    '../../tokens/date-navigator-tokens.css',
    './date-navigator.component.css',
  ],
  templateUrl: './date-navigator.component.html',
  host: {
    'role': 'group',
    'aria-label': 'Date navigation',
    '[class]': 'hostClasses()',
  },
})
export class DateNavigatorComponent {
  /** Formatted date label displayed between the arrows. */
  label = input.required<string>();

  /** Optional weekday name (e.g. "Martes") shown in bold before the label. */
  weekday = input<string>('');

  /** Icon displayed before the label. Default: 📅 */
  icon = input<string>('📅');

  /** Size variant. */
  size = input<DateNavigatorSize>('md');

  /** Semantic color for the nav arrow buttons. */
  color = input<DateNavigatorColor>('neutral');

  /** When true, prev/next buttons are disabled. */
  disabled = input<boolean>(false);

  /** When true, only the previous arrow is disabled (e.g. can't navigate before today). */
  prevDisabled = input<boolean>(false);

  /** When true, only the next arrow is disabled. */
  nextDisabled = input<boolean>(false);

  /** Shows an extra pair of double-chevron arrows (week jump) flanking the day arrows, inside the same pill. */
  showWeekJump = input<boolean>(false);

  /** Shows an extra pair of skip-to-edge arrows (month jump) outermost, inside the same pill. */
  showMonthJump = input<boolean>(false);

  /** Tooltip for the week-jump previous arrow. */
  weekPrevTitle = input<string>('Semana anterior');

  /** Tooltip for the week-jump next arrow. */
  weekNextTitle = input<string>('Semana siguiente');

  /** Tooltip for the month-jump previous arrow. */
  monthPrevTitle = input<string>('Mes anterior');

  /** Tooltip for the month-jump next arrow. */
  monthNextTitle = input<string>('Mes siguiente');

  /** Emits when the user clicks the previous arrow. */
  prev = output<void>();

  /** Emits when the user clicks the next arrow. */
  next = output<void>();

  /** Emits when the user clicks the week-jump previous (double chevron) arrow. */
  weekPrev = output<void>();

  /** Emits when the user clicks the week-jump next (double chevron) arrow. */
  weekNext = output<void>();

  /** Emits when the user clicks the month-jump previous (skip-to-edge) arrow. */
  monthPrev = output<void>();

  /** Emits when the user clicks the month-jump next (skip-to-edge) arrow. */
  monthNext = output<void>();

  /** SVG chevron-left icon (16×16) — single step (day). */
  protected readonly chevronLeft = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10 12L6 8L10 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  /** SVG chevron-right icon (16×16) — single step (day). */
  protected readonly chevronRight = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 4L10 8L6 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  /** SVG double-chevron-left icon (14×14) — medium step (week). */
  protected readonly chevronDoubleLeft = '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9 3L5 7L9 11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M5 3L1 7L5 11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  /** SVG double-chevron-right icon (14×14) — medium step (week). */
  protected readonly chevronDoubleRight = '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5 3L9 7L5 11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M9 3L13 7L9 11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  /** SVG skip-to-start icon (bar + triangle, 14×14) — large step (month). */
  protected readonly skipToStart = '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="2" y="3" width="1.5" height="8" fill="currentColor"/><path d="M11 3L6 7L11 11Z" fill="currentColor"/></svg>';

  /** SVG skip-to-end icon (triangle + bar, 14×14) — large step (month). */
  protected readonly skipToEnd = '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="10.5" y="3" width="1.5" height="8" fill="currentColor"/><path d="M3 3L8 7L3 11Z" fill="currentColor"/></svg>';

  readonly hostClasses = computed(() => {
    const size = this.size();
    const color = this.color();

    return {
      'msa-dn': true,
      [`msa-dn--${size}`]: true,
      [`msa-dn--${color}`]: true,
      'msa-dn--disabled': this.disabled(),
    };
  });

  readonly isPrevDisabled = computed(() => this.disabled() || this.prevDisabled());
  readonly isNextDisabled = computed(() => this.disabled() || this.nextDisabled());

  onPrev(): void {
    if (!this.isPrevDisabled()) {
      this.prev.emit();
    }
  }

  onNext(): void {
    if (!this.isNextDisabled()) {
      this.next.emit();
    }
  }

  onWeekPrev(): void {
    if (!this.isPrevDisabled()) {
      this.weekPrev.emit();
    }
  }

  onWeekNext(): void {
    if (!this.isNextDisabled()) {
      this.weekNext.emit();
    }
  }

  onMonthPrev(): void {
    if (!this.isPrevDisabled()) {
      this.monthPrev.emit();
    }
  }

  onMonthNext(): void {
    if (!this.isNextDisabled()) {
      this.monthNext.emit();
    }
  }
}
